/**
 * Schedule → Model list / isolate filters (colour + 3-week look-ahead).
 * Keep in sync with src/utils/schedule4dFilters.js.
 */

export function dayKey(value) {
  if (!value) return null;
  const s = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

function addUtcDays(day, days) {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function lookAheadWindow(fromDate, weeks = 3) {
  const from = dayKey(fromDate);
  if (!from) return null;
  const w = Number(weeks) > 0 ? Number(weeks) : 3;
  return { start: from, end: addUtcDays(from, w * 7) };
}

export function isActivityComplete(activity) {
  if (!activity) return false;
  if (activity.status === 'complete' || activity.colorKey === 'complete') return true;
  const pct = Number(activity.percentComplete);
  return Number.isFinite(pct) && pct >= 100;
}

/** Planned dates overlap [data date, data date + N weeks]. Complete work is out. Late remaining work stays in. */
export function activityInLookAhead(activity, fromDate, weeks = 3) {
  if (isActivityComplete(activity)) return false;
  if (activity.colorKey === 'late' || activity.status === 'late') return true;
  const win = lookAheadWindow(fromDate, weeks);
  if (!win) return false;
  const start = dayKey(activity.plannedStart);
  const finish = dayKey(activity.plannedFinish) || start;
  if (!start) return false;
  return start <= win.end && finish >= win.start;
}

export function activityColorBucket(activity, { mode, asOf } = {}) {
  if (mode === 'planned') {
    const start = dayKey(activity.plannedStart);
    const finish = dayKey(activity.plannedFinish);
    if (!asOf || !start) return 'not_started';
    if (start > asOf) return 'not_started';
    if (finish && finish <= asOf) return 'complete';
    return 'in_progress';
  }
  return activity.colorKey || 'not_started';
}

function isActivityMapped(activity, opts) {
  if (typeof opts.isMapped === 'function') return Boolean(opts.isMapped(activity));
  if (opts.mapped === undefined) return true;
  return Boolean(opts.mapped);
}

export function activityMatchesFilters(activity, opts = {}) {
  const {
    query = '',
    colorKeys = [],
    lookAheadWeeks = 0,
    fromDate = null,
    mode = 'status',
    asOf = null,
  } = opts;

  if (query && query.trim()) {
    const q = query.trim().toLowerCase();
    const hay = [activity.activityId, activity.name, activity.wbsPath]
      .map((v) => String(v || '').toLowerCase());
    if (!hay.some((h) => h.includes(q))) return false;
  }

  if (Number(lookAheadWeeks) > 0 && !activityInLookAhead(activity, fromDate, lookAheadWeeks)) {
    return false;
  }

  if (colorKeys && colorKeys.length) {
    const mapped = isActivityMapped(activity, opts);
    const statusKey = activityColorBucket(activity, { mode, asOf });
    const wantsUnmapped = colorKeys.includes('unmapped');
    const wantsStatus = colorKeys.some((k) => k !== 'unmapped');
    const hitUnmapped = wantsUnmapped && !mapped;
    const hitStatus = wantsStatus && colorKeys.includes(statusKey);
    if (!hitUnmapped && !hitStatus) return false;
  }

  return true;
}

export function collectDbIds(activities, byActivity) {
  const ids = [];
  for (const act of activities || []) {
    const hit = byActivity && typeof byActivity.get === 'function'
      ? byActivity.get(act.activityId)
      : null;
    if (Array.isArray(hit)) ids.push(...hit);
  }
  return [...new Set(ids.map(Number).filter((n) => Number.isFinite(n)))];
}
